import { useEffect, useRef, useState } from 'react';
import type { IPipelineStatusSummary, ISyncStatusSummaryResponse } from '@skystone/shared';
import { fabricSyncApi } from '@/services/fabric-sync-api';
import { pipelineStatusApi } from '@/services/pipeline-status-api';

const POLL_INTERVAL_MS = 15 * 60 * 1000;

interface UseHeaderStatusResult {
  sync: ISyncStatusSummaryResponse | null;
  syncLoading: boolean;
  pipeline: IPipelineStatusSummary | null;
  pipelineLoading: boolean;
}

export function useHeaderStatus(): UseHeaderStatusResult {
  const [sync, setSync] = useState<ISyncStatusSummaryResponse | null>(null);
  const [syncLoading, setSyncLoading] = useState(true);
  const [pipeline, setPipeline] = useState<IPipelineStatusSummary | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);

  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    async function loadSync(): Promise<void> {
      try {
        const data = await fabricSyncApi.getSyncStatusSummary();
        if (!cancelledRef.current) setSync(data);
      } catch (error) {
        console.error('[useHeaderStatus] sync summary failed', error);
        if (!cancelledRef.current) setSync(null);
      } finally {
        if (!cancelledRef.current) setSyncLoading(false);
      }
    }

    async function loadPipeline(): Promise<void> {
      try {
        const data = await pipelineStatusApi.getSummary();
        if (!cancelledRef.current) setPipeline(data);
      } catch (error) {
        console.error('[useHeaderStatus] pipeline summary failed', error);
        if (!cancelledRef.current) setPipeline(null);
      } finally {
        if (!cancelledRef.current) setPipelineLoading(false);
      }
    }

    function refetchAll(): void {
      void loadSync();
      void loadPipeline();
    }

    refetchAll();

    const intervalId = globalThis.setInterval(refetchAll, POLL_INTERVAL_MS);
    globalThis.addEventListener('focus', refetchAll);

    return () => {
      cancelledRef.current = true;
      globalThis.clearInterval(intervalId);
      globalThis.removeEventListener('focus', refetchAll);
    };
  }, []);

  return { sync, syncLoading, pipeline, pipelineLoading };
}
